/**
 * FromVerseDialog — quick-action shortcut: paste a Bible verse reference
 * (e.g., "Romans 8:28"), AI generates a sermon outline, then materializes it
 * into a real SlideCon project via the existing PreachKit pipeline.
 *
 * Two-step chain: POST /api/pulpit/sermon-outline → POST /api/pulpit/send-to-editor
 * → navigate to /editor/{project_id}. Failures at either step surface a toast.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookOpen, Sparkles, Loader2 } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLE_VERSES = [
  "Romans 8:28",
  "Psalm 23",
  "John 3:16",
  "Philippians 4:6-7",
  "Matthew 6:33",
];

const TONES = [
  { value: "encouraging",   label: "Encouraging" },
  { value: "expository",    label: "Expository" },
  { value: "prophetic",     label: "Prophetic" },
  { value: "evangelistic",  label: "Evangelistic" },
];

export default function FromVerseDialog({ t, open, onOpenChange }) {
  const navigate = useNavigate();
  const [verse, setVerse] = useState("");
  const [tone, setTone] = useState("encouraging");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(""); // "outline" | "slides"

  const reset = () => {
    setVerse("");
    setTone("encouraging");
    setBusy(false);
    setStep("");
  };

  const generate = async () => {
    const ref = verse.trim();
    if (!ref) {
      toast.error(t("verse_required") || "Enter a Bible verse reference (e.g. 'Romans 8:28')");
      return;
    }
    setBusy(true);
    setStep("outline");
    try {
      // Step 1 — outline from scripture
      const outlineRes = await axios.post(`${API}/pulpit/sermon-outline`, {
        scripture: ref,
        tone,
        length: "medium",
      });
      const outline = outlineRes.data?.outline;
      if (!outline) throw new Error("No outline returned");

      // Step 2 — materialize into a real project
      setStep("slides");
      const editorRes = await axios.post(`${API}/pulpit/send-to-editor`, {
        title: outline.title || ref,
        sermon_type: "outline",
        content: outline,
      });
      const pid = editorRes.data?.project_id;
      if (!pid) throw new Error("No project_id returned");

      toast.success(t("verse_slides_ready") || "Slides ready! Opening editor…");
      onOpenChange(false);
      reset();
      navigate(`/editor/${pid}`);
    } catch (err) {
      toast.error(getErrorMessage(err, t("verse_generation_failed") || "Could not build slides from this verse"));
    } finally {
      setBusy(false);
      setStep("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="from-verse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-amber-500" />
            {t("from_verse_title") || "Sermon from a Bible verse"}
          </DialogTitle>
          <DialogDescription>
            {t("from_verse_desc") || "Paste a verse reference — PreachKit drafts a 3-point outline and turns it into a slide deck."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="from-verse-input">{t("verse_reference") || "Verse reference"}</Label>
            <Input
              id="from-verse-input"
              value={verse}
              onChange={(e) => setVerse(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) generate(); }}
              placeholder="Romans 8:28"
              disabled={busy}
              data-testid="from-verse-input"
            />
            <div className="flex flex-wrap gap-1 pt-1">
              <span className="text-[10px] uppercase text-slate-500">{t("try") || "Try"}:</span>
              {EXAMPLE_VERSES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVerse(v)}
                  disabled={busy}
                  data-testid={`from-verse-example-${v.replace(/[^a-z0-9]/gi, "-")}`}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40"
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("tone") || "Tone"}</Label>
            <Select value={tone} onValueChange={setTone} disabled={busy}>
              <SelectTrigger data-testid="from-verse-tone-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((tn) => (
                  <SelectItem key={tn.value} value={tn.value} data-testid={`from-verse-tone-${tn.value}`}>
                    {tn.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {busy && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 text-xs text-amber-300 p-2 rounded bg-amber-500/10"
              data-testid="from-verse-progress"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {step === "outline" && (t("from_verse_step_outline") || "Drafting the outline…")}
              {step === "slides"  && (t("from_verse_step_slides")  || "Building slides…")}
            </motion.div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { if (!busy) { onOpenChange(false); reset(); } }}
            disabled={busy}
            data-testid="from-verse-cancel"
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={generate}
            disabled={busy || !verse.trim()}
            className="bg-amber-600 hover:bg-amber-700 gap-2"
            data-testid="from-verse-generate"
          >
            {busy
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Sparkles className="w-4 h-4" />}
            {busy ? (t("generating") || "Generating…") : (t("generate_slides") || "Generate Slides")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
